import { useState, useRef, useEffect, useCallback } from "react"
import {Info} from 'lucide-react'

interface TooltipProps {
  term: string
  definition: string
  variant?: "light" | "dark"
  className?: string
}

export default function Tooltip({ term, definition, variant = "light", className = "" }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<"top" | "bottom">("top")
  const triggerRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceAbove = rect.top
    setPosition(spaceAbove < 120 ? "bottom" : "top")
  }, [])

  useEffect(() => {
    if (isVisible) updatePosition()
  }, [isVisible, updatePosition])

  const show = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsVisible(true)
  }

  const hide = () => {
    timeoutRef.current = setTimeout(() => setIsVisible(false), 150)
  }

  const isDark = variant === "dark"

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={() => setIsVisible((v) => !v)}
        aria-label={`What is ${term}?`}
        className={`inline-flex items-center gap-1 cursor-help transition-colors duration-150 ${
          isDark
            ? "text-gray-400 hover:text-emerald-400"
            : "text-slate-400 hover:text-emerald-600"
        }`}
      >
        <Info className="w-3 h-3 flex-shrink-0" />
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          onMouseEnter={show}
          onMouseLeave={hide}
          role="tooltip"
          className={`absolute z-50 w-64 sm:w-72 px-4 py-3 rounded-xl shadow-xl border text-left animate-tooltip-in ${
            position === "top"
              ? "bottom-full mb-2 left-1/2 -translate-x-1/2"
              : "top-full mt-2 left-1/2 -translate-x-1/2"
          } ${
            isDark
              ? "bg-slate-700 border-slate-600 text-gray-200"
              : "bg-white border-gray-200 text-slate-700"
          }`}
        >
          {/* Arrow */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 border ${
              position === "top"
                ? `bottom-[-6px] border-t-0 border-l-0 ${isDark ? "bg-slate-700 border-slate-600" : "bg-white border-gray-200"}`
                : `top-[-6px] border-b-0 border-r-0 ${isDark ? "bg-slate-700 border-slate-600" : "bg-white border-gray-200"}`
            }`}
          />

          <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
            {term}
          </p>
          <p className={`text-xs leading-relaxed ${isDark ? "text-gray-300" : "text-slate-600"}`}>
            {definition}
          </p>
        </div>
      )}
    </span>
  )
}

// Pre-configured tooltips for the three key terms
export const TERM_DEFINITIONS = {
  BMR: "Basal Metabolic Rate — the number of calories your body burns at complete rest to maintain basic life functions like breathing, circulation, and cell production. Calculated using the Mifflin-St Jeor equation.",
  TDEE: "Total Daily Energy Expenditure — your BMR multiplied by an activity factor. This is the total calories you burn in a day including all movement and exercise. The key number for nutrition planning.",
  BMI: "Body Mass Index — a ratio of weight to height (kg/m\u00B2) used to categorize body composition. Useful as a general health indicator, but does not account for muscle mass or body fat distribution.",
} as const

export function BMRTooltip({ variant = "light", className = "" }: { variant?: "light" | "dark"; className?: string }) {
  return <Tooltip term="BMR" definition={TERM_DEFINITIONS.BMR} variant={variant} className={className} />
}

export function TDEETooltip({ variant = "light", className = "" }: { variant?: "light" | "dark"; className?: string }) {
  return <Tooltip term="TDEE" definition={TERM_DEFINITIONS.TDEE} variant={variant} className={className} />
}

export function BMITooltip({ variant = "light", className = "" }: { variant?: "light" | "dark"; className?: string }) {
  return <Tooltip term="BMI" definition={TERM_DEFINITIONS.BMI} variant={variant} className={className} />
}
